// REGISTRAR VISITA (venta) desde el manager — atajo al check-in real del kiosko.
// A diferencia de "ajustar saldo" (que solo mueve el monedero), esto registra una
// VISITA de verdad: aplica el % de cashback (o los puntos por visita), actualiza la
// última visita del cliente (Últimas Visitas / Inactivos), respeta el candado de
// tiempo entre visitas y dispara el mensaje automático de visita.
// Se usa desde: Historial del cliente y Lista de Clientes (acción rápida).
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Coins, X, Check, Loader2, Percent, Clock, Sparkles, Receipt, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { clientsApi } from '../../../api/clientsApi';
import { pctCashbackCliente } from '../../../utils/cashbackEspecial';

export interface VisitClient {
  id: string;
  name: string | null;
  phone?: string;
  tags?: string[];   // para la vista previa del cashback especial por etiqueta (17-oct-2026)
}

interface Props {
  client: VisitClient;
  onClose: () => void;
  // Se llama tras registrar con éxito (para refrescar saldo/movimientos en la página).
  onDone?: (r: { points_added: number; balance: number }) => void;
  /** Origen en el ledger: 'manager' (botón del panel) o 'scan' (tarjeta QR escaneada, 23-oct-2026). */
  source?: 'manager' | 'scan';
}

export default function RegisterVisitModal({ client, onClose, onDone, source = 'manager' }: Props) {
  const { currentUser } = useAuth();
  const isCashback = currentUser?.loyaltyMode === 'cashback';
  const pct = Number(currentUser?.cashbackPercentage ?? 10);
  const pointsPerVisit = Number(currentUser?.pointsPerVisit ?? 1);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  // Backdate (25-ago-2026, flag por negocio): fecha de la visita, default hoy. Solo se envía
  // al backend cuando es un día PASADO — el flujo normal (hoy) queda idéntico al de siempre.
  const canBackdate = !!currentUser?.backdateVisits;
  const todayIso = new Date().toLocaleDateString('sv-SE');  // YYYY-MM-DD en hora local
  const minIso = new Date(Date.now() - 90 * 864e5).toLocaleDateString('sv-SE');
  const [visitDate, setVisitDate] = useState(todayIso);
  // Folio del ticket (10-oct-2026, Bramarket) y sucursal: con 2+ sucursales se elige dónde fue la venta (antes quedaba
  // sin sucursal desde el panel) — el servidor valida que el usuario pueda usarla y que el folio no se repita en ella.
  const modoFolio = currentUser?.ticketRef || 'off';
  const [folio, setFolio] = useState('');
  const sucursales = (currentUser?.branches || []).filter((b) => b.isActive !== false);
  const [branchId, setBranchId] = useState<string>(sucursales.length === 1 ? String(sucursales[0].id) : '');
  const faltaFolio = modoFolio === 'required' && !folio.trim();
  // % de la sucursal elegida si tiene uno propio (8-oct-2026); si no, el general. Antes la vista previa decía 3 % y se abonaba 8 %.
  const pctSucursal = sucursales.find((b) => String(b.id) === branchId)?.cashbackPercentage;
  // Cashback especial por etiqueta (17-oct-2026): si el cliente la tiene, gana el mayor (igual que el servidor).
  const { pct: pctVista, especial: esEspecial } = pctCashbackCliente(currentUser, client.tags, pctSucursal != null ? Number(pctSucursal) : pct);
  const faltaSucursal = sucursales.length >= 2 && !branchId;

  const amountNum = parseFloat(amount) || 0;
  const preview = isCashback ? Math.round(amountNum * pctVista) / 100 : pointsPerVisit;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCashback && amountNum <= 0) { toast.error('Ingresa el monto de la venta.'); return; }
    if (faltaSucursal) { toast.error('Elige la sucursal de la venta.'); return; }
    if (faltaFolio) { toast.error('Captura el folio del ticket.'); return; }
    setSaving(true);
    try {
      const r = await clientsApi.checkin({
        contactId: client.id, amount: isCashback ? amountNum : 0,
        occurredAt: canBackdate && visitDate && visitDate < todayIso ? visitDate : undefined,
        ticketRef: modoFolio !== 'off' && folio.trim() ? folio.trim() : undefined,
        branchId: branchId || undefined,
        source,
      });
      if (r.was_balance_query) {
        toast('Visita muy reciente: se registró como consulta de saldo, sin abono (candado de tiempo entre visitas del programa).', { icon: '⏱️', duration: 6000 });
      } else {
        const abono = isCashback ? `$${r.points_added}` : `${r.points_added} pts`;
        const saldo = isCashback ? `$${r.balance}` : `${r.balance} pts`;
        toast.success(`Visita registrada: +${abono}${r.birthday_applied ? ' (x2 cumpleaños 🎂)' : ''} · Saldo: ${saldo}`);
      }
      onDone?.({ points_added: r.points_added, balance: r.balance });
      onClose();
    } catch (err) {
      toast.error((err as Error).message || 'No se pudo registrar la visita.');
    } finally { setSaving(false); }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="modal-sheet w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <Coins size={18} className="text-accent" /> Registrar visita
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <p className="mb-4 text-sm text-content-2">
          {client.name || 'Cliente'}{client.phone ? ` · ${client.phone}` : ''}
        </p>

        {isCashback ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Monto de la venta</label>
            <div className="relative max-w-[220px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-content-3 text-sm">$</span>
              <input
                type="number" min="0.01" step="0.01" autoFocus required
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-lg border border-line bg-surface-2 py-2.5 pl-7 pr-3 text-sm text-content outline-none focus:border-accent/50"
              />
            </div>
            {amountNum > 0 && (
              <p className="mt-2 flex items-center text-sm text-green-600 dark:text-green-400">
                <Percent className="mr-1 h-4 w-4" /> Cashback {pctVista}%{esEspecial ? ' (especial)' : ''}: se abonarán <b className="mx-1">${preview.toFixed(2)}</b> a su monedero.
              </p>
            )}
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-sm text-content-2">
            <Sparkles size={15} className="text-accent" /> Se sumará la visita con <b>{pointsPerVisit} pts</b> del programa.
          </p>
        )}

        {sucursales.length >= 2 && (
          <div className="mt-4">
            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-content-2"><MapPin size={12} /> Sucursal de la venta</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="visita-sucursal"
              className="w-full max-w-[260px] rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50">
              <option value="">Elige…</option>
              {sucursales.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
            </select>
          </div>
        )}

        {modoFolio !== 'off' && (
          <div className="mt-4">
            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-content-2">
              <Receipt size={12} /> Folio del ticket{modoFolio === 'required' ? '' : ' (opcional)'}
            </label>
            <input value={folio} onChange={(e) => setFolio(e.target.value)} maxLength={40} data-testid="visita-folio"
              placeholder="Ej. 104523"
              className="w-full max-w-[260px] rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50" />
          </div>
        )}

        {canBackdate && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-content-2">¿Cuándo fue la visita?</label>
            <input
              type="date" value={visitDate} min={minIso} max={todayIso}
              onChange={(e) => setVisitDate(e.target.value)}
              className="w-full max-w-[220px] rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50"
            />
            {visitDate && visitDate < todayIso && (
              <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                Se registrará con fecha del {new Date(visitDate + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}. El mensaje al cliente sale hoy.
              </p>
            )}
          </div>
        )}

        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-content-3">
          <Clock size={13} className="mt-0.5 shrink-0" />
          Registra una visita real: cuenta para Últimas Visitas e Inactivos y le llega su mensaje automático.
          Si la visita anterior fue hace muy poco, el programa la trata como consulta (sin abono).
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={saving || (isCashback && amountNum <= 0) || faltaFolio || faltaSucursal}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Registrar visita
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
